// Types for the derived-campaigns feature. A campaign here is NOT a stored
// row — it's a connected component of the (entry <-> identifier) graph,
// computed on read by unified_entries_flat: a domain is a campaign, an
// entity_reference is a campaign, and identifiers that co-occur on the same
// entry merge into one. See getCampaignsFlat/getCampaignDetail in
// backend/queries/postManagement.ts for the full rules.
//
// campaignId is NOT stable — it's the lowest-sorting identifier in the
// component, and a later entry bridging two components merges them,
// changing which id wins. Never store/bookmark it; always treat it as
// resolved fresh for the current page view. If you need a durable link to
// "this campaign", hold a domainId/entityReferenceId and re-resolve through
// getCampaignsFlat/getCampaignDetail instead.

// The campaign's identifier set (campaign_domains / campaign_entity_references
// on the view) — the whole component's, which is a SUPERSET of any one member
// entry's own links. An entry's own are entry_domains/entry_entity_references
// below; the difference between the two is exactly what explains why two
// entries are grouped, so don't substitute one for the other.
export type CampaignDomainIdentifier = {
  domain_id: string
  domain_url: string
}

export type CampaignEntityReferenceIdentifier = {
  entity_reference_id: string
  entity: string
  reference: string
}

// A single entry's own links (entry_domains / entry_entity_references). Richer
// than the campaign-level shape: each element carries the link row's own id —
// user_entry_domain_id is the FK entry_domain_data/entry_browser_data/
// domain_probe_run hang off, i.e. the route into that domain's probe history —
// plus the original_url as it was actually submitted.
export type EntryDomainLink = {
  user_entry_domain_id: string
  domain_id: string
  domain_url: string
  original_url: string | null
  manual_inactive: boolean | null
}

export type EntryEntityReferenceLink = {
  user_entry_entityreference_id: string
  entity_reference_id: string
  entity: string
  reference: string
  beneficiary: string | null
}

// One row of the campaigns list — a representative summary, not a stored
// entity. campaignType is 'domain' | 'multiple_domain' | 'entity_reference' |
// 'multiple_entity_reference', or a '+'-joined combination (e.g.
// 'domain+multiple_entity_reference').
export type CampaignSummary = {
  campaignId: string
  campaignType: string | null
  domainCount: number
  entityReferenceCount: number
  entryCount: number
  domains: CampaignDomainIdentifier[]
  entityReferences: CampaignEntityReferenceIdentifier[]
  lastCreatedAt: string
}

// Counts, not a campaignType string match: campaign_type is '+'-joined for
// mixed components, so a real multi-identifier campaign can be typed
// 'domain+multiple_entity_reference' — which doesn't start with "multiple".
// Mirrors the backend's own onlyMulti predicate in getCampaignsFlat exactly,
// so the client-side highlight can't disagree with the server-side filter.
export function isMultiIdentifierCampaign(
  c: Pick<CampaignSummary, 'entryCount' | 'domainCount' | 'entityReferenceCount'>,
): boolean {
  return c.entryCount > 1 || c.domainCount > 1 || c.entityReferenceCount > 1
}
